import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Form as FormikForm } from 'formik';

import { FormInstance } from 'types/step';
import { TopBar } from 'topBar/components/TopBar';
import { General } from 'icons/untitledui';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';

import getFormDefinition, {
  getFilledData,
  getInitialValues,
} from 'form/helpers/formFillingUtils';
import { usePosthogCapture } from 'form/posthogCapture/_index';
import { FormFieldRenderer } from 'form/components/FormFieldRenderer/_index';
import { FormOrigin } from 'form/types';

import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Text from 'theme/components/Text.style';
import { BaseCard } from 'theme/components/Cards.style';
import * as Styled from 'form/styles/FormEditView.style';
import * as FormStyled from 'form/styles/Form.style';

interface FormEditViewProps {
  form: FormInstance;
  origin: FormOrigin;
  onSubmit: (filledData: Record<string, any>) => void;
  onBack?: () => void;
  embedded?: boolean;
}

export default function FormEditView({
  form,
  origin,
  onSubmit,
  onBack,
  embedded = false,
}: FormEditViewProps): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation('pageForm');
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef);
  const posthogCapture = usePosthogCapture();

  const templateName =
    form.form_template.document_translation.form_template_name ||
    form.form_template.document_translation.title;

  const formDefinition = useMemo(
    () => getFormDefinition(form.form_template.placeholders),
    [form.form_template.placeholders]
  );

  const initialValues = useMemo(
    () => getInitialValues(formDefinition, form.filled_data),
    [formDefinition, form.filled_data]
  );

  useEffect(() => {
    posthogCapture.editViewOpened({
      templateId: form.form_template.id,
      templateName,
      origin,
    });
  }, [form.form_template.id, templateName, origin, posthogCapture]);

  const handleBack = () => {
    posthogCapture.editCancelled({
      templateId: form.form_template.id,
      templateName,
    });
    if (onBack) {
      onBack();
    } else {
      history.goBack();
    }
  };

  const handleSubmit = (values: Record<string, any>) => {
    const filledData = getFilledData(formDefinition, values);
    posthogCapture.formSubmitted({
      templateId: form.form_template.id,
      templateName,
    });
    onSubmit(filledData);
  };

  const ContentEl = embedded ? FormStyled.EmbeddedContent : PageLayout.Content;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ submitForm }) => (
        <PageLayout.ContentContainer>
          <TopBar
            onBackClick={handleBack}
            backButtonAriaLabel={t('form.cancel')}
            menuItems={[
              {
                label: t('edit.submit'),
                onClick: submitForm,
                icon: <General.Check size={20} />,
                primary: true,
              },
            ]}
            showOnDesktop={true}
            title={templateName}
            showTitle={isScrolledPast}
          />
          <ContentEl>
            <Styled.Container>
              <Styled.TitleGroup ref={titleRef}>
                <Text.H3>{templateName}</Text.H3>
                <Styled.Subtitle>
                  {t('edit.subtitle', { templateName })}
                </Styled.Subtitle>
              </Styled.TitleGroup>
              <FormikForm>
                <BaseCard>
                  {formDefinition.map((placeholder) => (
                    <FormFieldRenderer
                      key={placeholder.slug}
                      placeholder={placeholder}
                    />
                  ))}
                </BaseCard>
              </FormikForm>
            </Styled.Container>
          </ContentEl>
        </PageLayout.ContentContainer>
      )}
    </Formik>
  );
}
